import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import candidateApi, { getCandidateToken } from '../../utils/candidateApi';
import { logoutCandidateEverywhere, subscribeToLogout } from '../../utils/identity';
import api from '../../utils/api';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import StageBadge from '../../components/ui/StageBadge';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import ScoreRing from '../../components/ScoreRing';
import { MCQ_ASSESSMENT_TYPES } from '../../utils/constants';
import Input from '../../components/ui/Input';
import CareerPublicContent from '../../components/public/CareerPublicContent';
import StageTimeline from '../../components/StageTimeline';
import { safeHttpUrl } from '../../utils/safeUrl';
import PageError from '../_shared/PageError';
import { formatApiError } from '../../utils/errors';
import toast from 'react-hot-toast';

const TABS = [
  { id: 'home', label: 'Home' },
  { id: 'profile', label: 'Profile' },
  { id: 'assessments', label: 'Assessments' },
  { id: 'settings', label: 'Settings' },
  { id: 'help', label: 'Help' },
];

const NOTICE_UNITS = ['Days', 'Weeks', 'Months'];

export default function CandidatePortal() {
  const navigate = useNavigate();
  const [candidateData, setCandidateData] = useState(null);
  const [details, setDetails] = useState(null);
  const [careerData, setCareerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestMode, setGuestMode] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState('home');
  const [timedTests, setTimedTests] = useState([]);
  const [testsError, setTestsError] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [runnerIndex, setRunnerIndex] = useState(0);
  const [runnerLoading, setRunnerLoading] = useState(false);
  const [runnerSaving, setRunnerSaving] = useState(false);

  const loadPortal = useCallback(() => {
    return candidateApi.get('/candidates/portal/me')
      .then(r => {
        setDetails(r.data);
        setCandidateData(r.data.candidate);
        setLoadError(null);
        return true;
      })
      .catch(err => {
        // A 401 is handled by the candidate API client, which drops the token
        // and sends the candidate to the login screen.
        setLoadError(err);
        return false;
      });
  }, []);

  // Failures must surface: swallowing them showed "no assessments" to a
  // candidate whose session had simply expired.
  const loadTimedTests = useCallback(() => {
    return candidateApi.get('/candidate-assessments/me')
      .then(r => {
        setTimedTests(r.data?.tests || []);
        setTestsError(null);
        return true;
      })
      .catch(err => {
        setTestsError(err);
        return false;
      });
  }, []);

  // The portal doubles as a public page, so a visitor with no token gets guest
  // mode without a request that the 401 interceptor would turn into a redirect.
  useEffect(() => {
    const signedIn = !!getCandidateToken();
    if (!signedIn) setGuestMode(true);
    Promise.all([
      api.get('/career/public').then(r => setCareerData(r.data)).catch(() => setCareerData(null)),
      ...(signedIn ? [loadPortal(), loadTimedTests()] : []),
    ]).finally(() => setLoading(false));
  }, [loadPortal, loadTimedTests]);

  // A sign-out in another tab must not leave this one showing cached PII.
  useEffect(() => subscribeToLogout(() => {
    setGuestMode(true);
    setCandidateData(null);
    setDetails(null);
    setTimedTests([]);
    setActiveSession(null);
  }), []);

  async function logout() {
    // Revokes the token server-side and clears every identity in every tab;
    // nulling the local token alone left the session valid.
    await logoutCandidateEverywhere();
    setGuestMode(true);
    setCandidateData(null);
    navigate('/portal');
  }

  async function saveSettingsNotifications(e) {
    e.preventDefault();
    const el = e.currentTarget.querySelector('input[name="portal_email_notifications"]');
    const v = el?.checked === true;
    try {
      await candidateApi.patch('/candidates/portal/me/settings', { portal_email_notifications: v });
      await loadPortal();
      toast.success('Settings saved');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save your preferences.'));
    }
  }

  async function submitTechTask(taskId, formEl) {
    const fd = new FormData(formEl);
    try {
      await candidateApi.post(`/techtasks/task/${taskId}/submit`, fd);
      toast.success('Submission sent');
      await loadPortal();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not send your submission.'));
    }
  }

  async function openSession(sessionId) {
    setRunnerLoading(true);
    try {
      const [qRes, sRes] = await Promise.all([
        candidateApi.get(`/candidate-assessments/${sessionId}/questions`),
        candidateApi.get(`/candidate-assessments/${sessionId}/state`),
      ]);
      setActiveSession({
        ...qRes.data.session,
        questions: qRes.data.questions || [],
        remaining_seconds: sRes.data?.remaining_seconds ?? qRes.data.session?.remaining_seconds ?? 0,
        answered_count: sRes.data?.answered_count ?? 0,
      });
      setRunnerIndex(0);
      setTab('assessments');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not open assessment'));
    } finally {
      setRunnerLoading(false);
    }
  }

  async function startOrResume(testType) {
    try {
      const r = await candidateApi.post(`/candidate-assessments/${encodeURIComponent(testType)}/start`);
      await openSession(r.data.session_id);
      await loadTimedTests();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not start assessment'));
    }
  }

  async function saveAnswer(questionId, selectedIdx) {
    if (!activeSession?.id) return;
    setRunnerSaving(true);
    try {
      await candidateApi.post(`/candidate-assessments/${activeSession.id}/answer`, {
        question_id: questionId,
        selected_idx: selectedIdx,
      });
      setActiveSession(s => {
        if (!s) return s;
        const questions = s.questions.map(q => (q.id === questionId ? { ...q, selected_idx: selectedIdx } : q));
        return { ...s, questions };
      });
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save answer'));
    } finally {
      setRunnerSaving(false);
    }
  }

  async function submitSession() {
    if (!activeSession?.id) return;
    setRunnerSaving(true);
    try {
      const r = await candidateApi.post(`/candidate-assessments/${activeSession.id}/submit`);
      toast.success(`Submitted. Score: ${r.data.score}%`);
      setActiveSession(null);
      await Promise.all([loadPortal(), loadTimedTests()]);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not submit assessment'));
    } finally {
      setRunnerSaving(false);
    }
  }

  useEffect(() => {
    if (!activeSession?.id) return undefined;
    const t = setInterval(async () => {
      setActiveSession(prev => {
        if (!prev) return prev;
        return { ...prev, remaining_seconds: Math.max(0, (prev.remaining_seconds || 0) - 1) };
      });
      try {
        const r = await candidateApi.get(`/candidate-assessments/${activeSession.id}/state`);
        setActiveSession(prev => (prev ? { ...prev, remaining_seconds: r.data.remaining_seconds } : prev));
      } catch {
        // Transient polling errors are ignored; a 401 is escalated by the API
        // client, so answers are never typed into a session that is already dead.
      }
    }, 5000);
    return () => clearInterval(t);
  }, [activeSession?.id]);

  if (loading) return <Spinner fullPage />;

  if (guestMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12" data-testid="portal-page">
        <Card className="max-w-md w-full text-center p-8 shadow-sm" data-testid="portal-guest">
          <h1 className="text-xl font-bold text-gray-900">Candidate Portal</h1>
          <p className="text-sm text-gray-500 mt-2">
            Sign in with the email you used to apply, or create an account if you have an application code.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <Button onClick={() => navigate('/candidate/login')} data-testid="portal-sign-in">Sign in</Button>
            <Button variant="secondary" onClick={() => navigate('/candidate/signup')} data-testid="portal-signup">Create account</Button>
          </div>
          <p className="text-xs text-gray-400 mt-6">
            <Link to="/track" className="text-brand-600 hover:underline">Track your application</Link>
            {' · '}
            <Link to="/careers" className="text-brand-600 hover:underline">Careers</Link>
          </p>
        </Card>
        {careerData && (
          <div className="mt-8 max-w-3xl w-full rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
            <p className="text-xs text-gray-400 px-4 py-2 bg-gray-50 border-b">Same employer content as the public careers page</p>
            <CareerPublicContent data={careerData} readOnly />
          </div>
        )}
      </div>
    );
  }

  if (!candidateData) {
    if (loadError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md w-full">
            <PageError
              title="We couldn't load your portal"
              error={loadError}
              onRetry={loadPortal}
            >
              <Link to="/track" className="text-sm text-brand-700 underline hover:text-brand-800">
                Track your application with your HIQ code instead
              </Link>
            </PageError>
          </div>
        </div>
      );
    }
    return null;
  }

  const locked = candidateData.locked;
  const stageHistory = details?.stageHistory || [];
  const latestStageUpdate = stageHistory.length ? stageHistory[stageHistory.length - 1] : null;
  const assignedTechTasks = (details?.techTasks || []).filter(t => t.status === 'assigned');

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Candidate Portal</h1>
          <p className="text-sm text-gray-500">Welcome, {candidateData.first_name}</p>
        </div>
        <Button variant="ghost" onClick={logout}>Sign Out</Button>
      </header>

      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{candidateData.first_name} {candidateData.last_name}</h2>
              <p className="text-gray-500 text-sm">{candidateData.position} — {candidateData.department}</p>
              <p className="text-xs text-gray-400 mt-1">Code: {candidateData.app_code}</p>
            </div>
            <StageBadge stage={candidateData.stage} />
          </div>
        </Card>

        {locked && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Your profile is locked by the hiring team. You can still view your application and materials.
          </p>
        )}

        <div className="flex gap-2 border-b flex-wrap">
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'home' && (
          <div className="space-y-6">
            <Card title="Application progress">
              <p className="text-sm text-gray-600 mb-3">
                Your current stage and recent moves in the hiring pipeline.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs text-gray-500">Current stage</span>
                <StageBadge stage={candidateData.stage} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Current stage</p>
                  <p className="text-sm font-semibold text-gray-900">{candidateData.stage}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Latest update</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {latestStageUpdate ? new Date(latestStageUpdate.created_at).toLocaleDateString() : 'No updates yet'}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Assigned tasks</p>
                  <p className="text-sm font-semibold text-gray-900">{assignedTechTasks.length}</p>
                </div>
              </div>
              <StageTimeline history={details?.stageHistory || []} />
              <div className="mt-4">
                <Button size="sm" onClick={() => setTab('assessments')}>
                  Open and complete assessments
                </Button>
              </div>
            </Card>
            <div className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
              {careerData ? (
                <CareerPublicContent data={careerData} readOnly />
              ) : (
                <p className="p-6 text-sm text-gray-500">Career content could not be loaded.</p>
              )}
            </div>
          </div>
        )}

        {tab === 'profile' && (
          <Card title="Profile">
            <form
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              onSubmit={async (e) => {
                e.preventDefault();
                if (locked) return;
                const fd = new FormData(e.currentTarget);
                const payload = Object.fromEntries(fd.entries());
                try {
                  await candidateApi.patch('/candidates/portal/me/profile', payload);
                  toast.success('Profile updated');
                  await loadPortal();
                } catch (err) {
                  toast.error(formatApiError(err, 'Could not update your profile.'));
                }
              }}
            >
              <Input name="first_name" label="First name" defaultValue={candidateData.first_name || ''} />
              <Input name="last_name" label="Last name" defaultValue={candidateData.last_name || ''} />
              <Input name="email" label="Email" defaultValue={candidateData.email || ''} disabled />
              <Input name="phone" label="Phone" defaultValue={candidateData.phone || ''} />
              <Input name="current_title" label="Current title" defaultValue={candidateData.current_title || ''} />
              <Input name="employer" label="Employer" defaultValue={candidateData.employer || ''} />
              <Input name="current_salary" type="number" label="Current salary" defaultValue={candidateData.current_salary ?? ''} />
              <Input name="expected_salary" type="number" label="Expected salary" defaultValue={candidateData.expected_salary ?? ''} />
              <Input name="notice_period" type="number" label="Notice period" defaultValue={candidateData.notice_period ?? ''} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notice unit</label>
                <select name="notice_unit" className="input-field" defaultValue={candidateData.notice_unit || 'Days'}>
                  {NOTICE_UNITS.map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
              <Input name="portfolio_url" label="Portfolio URL" className="sm:col-span-2" defaultValue={candidateData.portfolio_url || ''} />
              <Input name="github_url" label="GitHub URL" className="sm:col-span-2" defaultValue={candidateData.github_url || ''} />
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Cover letter</label>
                <textarea
                  name="cover_letter"
                  className="input-field min-h-[100px]"
                  defaultValue={candidateData.cover_letter || ''}
                  readOnly={locked}
                />
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-gray-500 mb-2">Department and role are fixed from your application.</p>
                <Button type="submit" disabled={locked}>Save profile</Button>
              </div>
            </form>
          </Card>
        )}

        {tab === 'assessments' && (
          <div className="space-y-6">
            {testsError && (
              <PageError
                title="We couldn't load your assessments"
                error={testsError}
                onRetry={loadTimedTests}
              />
            )}
            {activeSession && (
              <Card title={`Timed assessment · ${activeSession.test_type}`}>
                {activeSession.questions?.length ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm text-gray-600">
                        Question {runnerIndex + 1} of {activeSession.questions.length}
                      </p>
                      <Badge className={activeSession.remaining_seconds <= 60 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}>
                        {Math.floor((activeSession.remaining_seconds || 0) / 60)}:{String((activeSession.remaining_seconds || 0) % 60).padStart(2, '0')}
                      </Badge>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-500"
                        style={{ width: `${Math.round(((runnerIndex + 1) / activeSession.questions.length) * 100)}%` }}
                      />
                    </div>
                    <div className="rounded-lg border border-gray-200 p-4">
                      <p className="text-sm font-medium text-gray-900 mb-3">{activeSession.questions[runnerIndex]?.question}</p>
                      <div className="space-y-2">
                        {(activeSession.questions[runnerIndex]?.options || []).map((opt, idx) => (
                          <label key={idx} className="flex items-start gap-2 text-sm cursor-pointer">
                            <input
                              type="radio"
                              name={`q_${activeSession.questions[runnerIndex].id}`}
                              checked={activeSession.questions[runnerIndex].selected_idx === idx}
                              onChange={() => saveAnswer(activeSession.questions[runnerIndex].id, idx)}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => setRunnerIndex(i => Math.max(0, i - 1))}
                        disabled={runnerIndex === 0 || !!activeSession.no_backtrack}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => setRunnerIndex(i => Math.min(activeSession.questions.length - 1, i + 1))}
                        disabled={runnerIndex >= activeSession.questions.length - 1}
                      >
                        Next
                      </Button>
                      <Button onClick={submitSession} loading={runnerSaving}>Submit test</Button>
                      <Button variant="ghost" onClick={() => setActiveSession(null)}>Close</Button>
                    </div>
                    {activeSession.no_backtrack && (
                      <p className="text-xs text-amber-700">No backtrack is enabled for this assessment.</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Loading questions...</p>
                )}
              </Card>
            )}
            <Card title="Your assessments">
              <div className="space-y-4">
                {MCQ_ASSESSMENT_TYPES.map((test_type) => {
                  const a = details?.assessments?.find(x => x.test_type === test_type);
                  const t = timedTests.find(x => x.test_type === test_type);
                  const displayStatus = a?.completed_at
                    ? (a.passed ? 'Completed · Passed' : 'Completed · Below pass')
                    : (t?.status === 'in_progress' ? 'In progress' : 'Assigned');
                  return (
                    <div
                      key={test_type}
                      className="p-4 rounded-lg border border-gray-200 flex flex-wrap items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <Badge className="bg-indigo-50 text-indigo-800 mr-2">Timed test</Badge>
                        <span className="font-medium text-sm text-gray-900">{test_type}</span>
                      </div>
                      {a?.completed_at ? (
                        <div className="flex items-center gap-3">
                          <ScoreRing value={a.score || 0} size={36} />
                          <Badge className={a.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}>
                            {a.passed ? 'Passed' : 'Below pass'}
                          </Badge>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {t?.status === 'in_progress' ? (
                            <Button size="sm" onClick={() => openSession(t.session_id)} loading={runnerLoading}>Resume test</Button>
                          ) : (
                            <Button size="sm" onClick={() => startOrResume(test_type)} loading={runnerLoading}>Start test</Button>
                          )}
                          <Badge className="bg-gray-100 text-gray-700">{displayStatus}</Badge>
                        </div>
                      )}
                    </div>
                  );
                })}

                {(details?.techTasks || []).map(t => (
                  <div key={t.id} className="p-4 rounded-lg border border-gray-200 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <Badge className="bg-orange-50 text-orange-900 mr-2">Technical task</Badge>
                        <span className="text-sm font-semibold text-gray-900">{t.title || 'Take-home assignment'}</span>
                        {t.category && (
                          <span className="block text-xs text-gray-500 mt-0.5">Category: {t.category}</span>
                        )}
                      </div>
                      <Badge className={
                        t.status === 'reviewed' ? 'bg-green-100 text-green-800'
                          : t.status === 'submitted' ? 'bg-blue-100 text-blue-800'
                          : 'bg-amber-100 text-amber-800'
                      }>{t.status}</Badge>
                    </div>
                    {t.prompt && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Task description</p>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap border-l-2 border-brand-200 pl-3">{t.prompt}</div>
                      </div>
                    )}
                    {t.deadline && (
                      <p className="text-xs text-gray-500">Deadline: {new Date(t.deadline).toLocaleDateString()}</p>
                    )}
                    {(t.submission_text || t.submission_url || t.submission_file) && (
                      <div className="text-sm bg-gray-50 rounded p-3 space-y-1">
                        <p className="font-medium text-gray-700">Your answer</p>
                        {t.submission_text && <p className="whitespace-pre-wrap">{t.submission_text}</p>}
                        {safeHttpUrl(t.submission_url) && (
                          <a
                            href={safeHttpUrl(t.submission_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-600 hover:underline break-all"
                          >
                            {t.submission_url}
                          </a>
                        )}
                        {t.submission_file && (
                          <p className="text-xs text-gray-600">File uploaded</p>
                        )}
                      </div>
                    )}
                    {t.feedback && (
                      <p className="text-sm text-blue-800 bg-blue-50 rounded p-2"><strong>Feedback:</strong> {t.feedback}</p>
                    )}
                    {t.reviewed_at && (
                      <p className="text-xs text-gray-500">Reviewed on: {new Date(t.reviewed_at).toLocaleString()}</p>
                    )}
                    {t.submitted_at && (
                      <p className="text-xs text-gray-500">Submitted on: {new Date(t.submitted_at).toLocaleString()}</p>
                    )}
                    {t.status !== 'reviewed' && !locked && (
                      <form
                        className="space-y-2 border-t border-gray-100 pt-3"
                        onSubmit={e => {
                          e.preventDefault();
                          submitTechTask(t.id, e.currentTarget);
                        }}
                      >
                        <label className="block text-xs font-medium text-gray-600">Your answer</label>
                        <textarea
                          name="submission_text"
                          className="input-field min-h-[100px]"
                          placeholder="Enter your response…"
                          defaultValue={t.submission_text || ''}
                        />
                        <Input name="submission_url" label="Link (optional)" placeholder="https://…" defaultValue={t.submission_url || ''} />
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Attach file (optional)</label>
                          <input type="file" name="file" className="text-sm" />
                        </div>
                        <Button type="submit" size="sm">{t.status === 'submitted' ? 'Update submission' : 'Complete and submit'}</Button>
                      </form>
                    )}
                    {t.status !== 'reviewed' && locked && (
                      <p className="text-xs text-gray-500">Submissions are locked for your account.</p>
                    )}
                  </div>
                ))}

                {!(details?.techTasks || []).length && (
                  <p className="text-sm text-gray-400">No technical tasks assigned.</p>
                )}
              </div>
            </Card>
          </div>
        )}

        {tab === 'settings' && (
          <Card title="Settings">
            <form onSubmit={saveSettingsNotifications} className="max-w-md space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="portal_email_notifications"
                  defaultChecked={candidateData.portal_email_notifications !== false}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-800">Email me about application updates and reminders</span>
              </label>
              <Button type="submit" size="sm">Save preferences</Button>
            </form>
          </Card>
        )}

        {tab === 'help' && (
          <Card title="Help & manuals">
            {details?.manuals?.length ? (
              <div className="space-y-3">
                {details.manuals.map(m => (
                  <details key={m.id} className="p-3 rounded-lg border border-gray-200">
                    <summary className="cursor-pointer font-medium text-gray-900">{m.title}</summary>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap mt-2">{m.body}</p>
                  </details>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No manuals available.</p>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
